<template>
  <div v-if="!purchaseError">
    <top-navbar
      v-if="!loadingInit && !isIframe && !isWaitingRoom"
      :only-logo="true"
      :show-sub-nabvar="false"
      :is-ticket-office="isTicketOffice"
    />
    <div v-if="event.channel?.channel === CHANNEL_ID_HERBALIFE" class="h-24" />
    <div
      v-else-if="
        accessCodeValid &&
          (!loadingInit || isWaitingRoom) &&
          !loading &&
          !isTicketOffice
      "
      class="w-full mx-auto pt-[18px] px-[11px] md:px-0"
      :class="isTicketOffice ? '' : 'max-w-2xl'"
    >
      <purchase-header
        :selected-section="0"
        :show-steps="purchaseModel !== 'external' && !showWaitingList"
      />
    </div>
    <div v-if="showWaitingList" class="w-full max-w-lg mx-auto">
      <waiting-list-tickets
        @accept="setPreferencesWaitingList"
        @cancel="showWaitingList = false"
      />
    </div>
    <div
      v-if="
        !showWaitingList &&
          accessCodeValid &&
          (!loadingInit || isWaitingRoom) &&
          !loading &&
          !isTicketOffice
      "
      class="w-full max-w-lg mx-auto mb-[50px]"
    >
      <tickets-list v-if="purchaseModel === 'normal'" />
      <waiting-list-advise
        v-if="showWaitingListAdvise && purchaseModel === 'normal'"
        @accept="acceptWaitingList"
        @delete="deleteWaitingList"
      />
      <div
        v-if="event.access_policy && purchaseModel === 'normal'"
        class="mt-8 text-xs px-[11px] md:px-0"
        :style="getPersonalizedText"
        v-html="event.access_policy.replace(/\n/g, '<br>')"
      />
      <span
        v-if="hasProwered"
        class="iframe-logo-wegow flex"
        :style="getPersonalizedText"
      >
        Powered by
        <img
          :src="require('@/assets/images/wegow-conciertos.svg')"
          alt="Conciertos Wegow"
        />
      </span>
    </div>
    <div
      v-if="
        !loadingInit &&
          !loading &&
          !showWaitingList &&
          purchaseModel === 'seatsio' &&
          accessCodeValid
      "
      class="px-2 md:px-24 w-full m-auto max-w-6xl items-center"
    >
      <seats-io />
      <div
        v-if="event.access_policy"
        :style="getPersonalizedText"
        class="mt-8 text-xs"
        v-html="event.access_policy.replace(/\n/g, '<br>')"
      />
    </div>
    <div
      v-if="
        !loadingInit &&
          !loading &&
          !showWaitingList &&
          purchaseModel === 'external' &&
          accessCodeValid
      "
      class="w-full max-w-4xl mx-auto"
    >
      <external-options-list />
    </div>
    <div v-if="isTicketOffice" class="flex">
      <purchase-square-header class="w-1/4 ml-auto mr-2" />
      <div class="w-2/4 ml-2 mr-auto overflow-y-auto mt-8 ticket-office-list">
        <tickets-list :ticket-office="true" />
        <purchase-banner :ticket-office="true" class="mt-auto" />
      </div>
    </div>
    <!-- <div class="h-40"></div> -->
  </div>
  <not-found v-else />
  <wegow-loader v-if="loadingInit || loading" class="min-h-screen" />
  <access-code-modal v-if="!accessCodeValid" />
  <purchase-banner v-if="!isTicketOffice" class="mt-auto" />

  <ga-pixel v-if="event?.google_pixel_js" :pixel="event.google_pixel_js" />
  <fb-pixel v-if="event?.facebook_pixel_js" :pixel="event.facebook_pixel_js" />
</template>

<script setup>
// Vue
import { computed, onBeforeMount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { event as tagEvent } from "vue-gtag";

// Composables
import { useUser } from "@/composables/useUser";
import { useEvent } from "@/composables/useEvent";
import { usePurchase } from "@/composables/usePurchase";
import { usePayment } from "@/composables/usePayment";
import { useWaitingRoom } from "@/composables/useWaitingRoom";
import { useModalHandler } from "@/plugins/useModalHandler";
import { useIframeStyle } from "@/composables/useIframeStyle";
import { useProduct } from "@/composables/useProduct";

// Components
import NotFound from "@/views/general/NotFound";
import TopNavbar from "@/components/general/ui/navbar/TopNavbar";
import WaitingListAdvise from "@/components/wegow/purchase/WaitingListAdvise";
import ExternalOptionsList from "@/components/wegow/purchase/ExternalOptionsList";
import SeatsIo from "@/components/wegow/purchase/seatsio/SeatsIo";
import PurchaseBanner from "@/components/wegow/purchase/PurchaseBanner";
import PurchaseHeader from "@/components/wegow/purchase/PurchaseHeader";
import PurchaseSquareHeader from "@/components/wegow/purchase/PurchaseSquareHeader";
import AccessCodeModal from "@/components/wegow/purchase/AccessCodeModal";
import TicketsList from "@/components/wegow/purchase/TicketsList";
import WaitingListTickets from "@/components/wegow/purchase/WaitingListTickets";
import WegowLoader from "@/components/general/ui/WegowLoader.vue";
import GaPixel from "@/components/general/GaPixel.vue";
import FbPixel from "@/components/general/FbPixel.vue";

// Modules
import { useStatsStore } from "@/stores/modules/stats";
import { useUserStore } from "@/stores/modules/user";

// Plugins
import { CHANNEL_ID_HERBALIFE } from "@/constants.js";
import { metas } from "@/plugins/mixins/metas";
import { setParameters } from "@/plugins/parameters";

// Variables
const route = useRoute();
const router = useRouter();
const {
  USER_NOLOGIN_INFO,
  openModal,
  openErrorModal,
  closeModal
} = useModalHandler();
const { isAuth, userDetail, loadingInit } = useUser();
const { isWaitingRoom } = useWaitingRoom();
const { t } = useI18n();
const {
  isIframe,
  details: event,
  tickets,
  iframeStyles,
  getDetails,
  getTickets,
  addToWaitingList
} = useEvent();
const {
  accessCodeValid,
  setReference,
  resetPurchase,
  setWhiteLabel,
  createNewPurchase
} = usePurchase();
const { productsPurchase, resetProductPurchase } = useProduct();
const { paymentMethods, getPaymentMethods } = usePayment();
const { getPersonalizedText } = useIframeStyle();
const loading = ref(false);
const isTicketOffice = ref(false);
const purchaseError = ref(false);
const showWaitingListAdvise = ref(false);
const showWaitingList = ref(false);
const whiteLabelLoginIds = ref([2, 3]);

const statsStore = useStatsStore();
const userStore = useUserStore();

// Computed
const purchaseModel = computed(() => {
  if (event.value?.ticket_distributions[0]?.system === "seatsio") {
    return "seatsio";
  } else if (
    event.value?.has_options &&
    event.value.options.length > 0 &&
    tickets.value.length === 0
  ) {
    return "external";
  } else {
    return "normal";
  }
});

const whiteLabel = computed(() => {
  if (event.value?.white_label) {
    return event.value?.white_label.find(
      whiteLabel => whiteLabel.id === Number(route.query.hb)
    );
  } else {
    return undefined;
  }
});

const hasProwered = computed(() => {
  if (
    (isWaitingRoom.value ||
      isIframe.value ||
      event.value?.white_label_related) &&
    iframeStyles.value?.show_media
  ) {
    if (whiteLabel.value && whiteLabel.value?.powered_by_wegow) {
      return true;
    } else {
      return false;
    }
  }
  return false;
});

const init = route =>
  (async route => {
    resetPurchase();

    if (productsPurchase.value && productsPurchase.value.length > 0) {
      resetProductPurchase();
    }

    loading.value = true;
    metas.methods.setLanguage();
    metas.methods.setCanonical(route);
    if (route.query.hb) {
      setWhiteLabel(route.query.hb);
    } else {
      setWhiteLabel("");
    }
    await getDetails(route.params.slug);
    if (
      event.value.id &&
      (event.value.closed ||
        !event.value.enabled ||
        !event.value.tickets_enabled)
    ) {
      window.location.href = event.value.permalink;
    } else if (event.value.access_code_needed) {
      accessCodeValid.value = false;
    } else if (event.value.id) {
      await configurePurchase(route);
      showWaitingListAdvise.value = event.value.showWhiteList;
      setMetas(route);
    } else {
      purchaseError.value = true;
    }

    // this.$gtag.pageview({
    //   page_path: window.location.pathname,
    //   page_title: window.location.page_title,
    //   page_location: window.location.href
    // })
    if (route.query.r) {
      tagEvent(route.query.r, {
        id: event.value.id,
        title: event.value.title,
        type: event.value.type === 0 ? "Concert" : "Festival"
      });
    }
    loading.value = false;
  })(route).catch(() => {
    loading.value = false;
    purchaseError.value = true;
  });

// LifeHooks
onBeforeMount(() => init(route));
onMounted(async () => {
  await router.isReady();
  if (route.query.u) {
    setParameters(`u-param-${route.name}`, route, "u");
  }
  if (route.query.r) {
    setParameters(`r-param-${route.name}`, route, "r");
    router.replace({ query: { ...query, r: localStorage.getItem("r-param") } });
  }
  await sendStats();
});

// Methods

const sendStats = async () => {
  // Enviamos los datos para las stats
  const params = {
    campaign: "purchase_view",
    id: userStore.userDetail.unsubscribe_token
      ? userStore.userDetail.unsubscribe_token
      : sessionStorage.tt_sessionId,
    uri: route.fullPath,
    pageName: route.name,
    queryParams: JSON.stringify(route.query)
  };
  if (!window.location.href.includes("wegowlocal")) {
    await statsStore.sendToStatsService(params);
  }
};

const setMetas = actualRoute => {
  // Metas
  let title = "";
  if (event.value.html_personalization) {
    title = event.value.html_personalization.head_title;
  } else if (event.value.type === 0) {
    let artists = event.value.artists
      .map(artist => artist?.name)
      .join(", ")
      .toString();
    title = t("general.metas.concertTitle", {
      artists: artists.length !== 0 ? artists : event.value.title,
      venue: event.value.venue.name,
      city: event.value.city.name
    });
  } else {
    title = t("general.metas.festivalTitle", {
      event_title: event.value.title
    }).substring(2);
  }
  metas.methods.setMetasHtml(
    {
      title: t("general.metas.purchaseTitle", { title: title }),
      noIndex: true
    },
    actualRoute,
    router
  );
};
 
const configurePurchase = async actualRoute => {
  if (iframeStyles.value.background_color) {
    document.getElementsByTagName("html")[0].style.backgroundColor =
      iframeStyles.value.background_color;
    document.body.style.backgroundColor = iframeStyles.value.background_color;
  }
  if (iframeStyles.value.button_color && document.styleSheets) {
    // To change scroll-bar color
    var style = document.createElement("style");
    style.type = "text/css";
    var css = `::-webkit-scrollbar-thumb {
        background-color: ${iframeStyles.value.button_color} !important;
      }`;
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }
  if (actualRoute.name === "PurchaseOfficePage") {
    // TODO: cambiar la ocndicion cuando este el back
    // if (
    //   !isAuth.value ||
    //   (!event.value.canShellOffice.includes(userDetail.value.id) &&
    //     !userDetail.value.is_staff)
    // ) {
    if (!isAuth.value) {
      router.push({ name: "EventProfile" });
    } else {
      isTicketOffice.value = true;
      await getTickets({ ticket_office: true });
      // Tendria que mirar si es ticket office?
      await getPaymentMethods(event.value.id, true);
      if (
        tickets.value.length === 0 ||
        (!paymentMethods.value.ticket_office_cash &&
          !paymentMethods.value.ticket_office_cards)
      ) {
        openErrorModal({
          modal: {
            size: "normal",
            generalClass: "text-center p-6",
            title: t("wegow.error.ticketOfficeConfigurationError"),
            description: t("wegow.error.contactSupport"),
            btn_message: t("wegow.error.returnEvent"),
            close_method: () => {
              closeModal();
              router.push({ name: "EventProfile" });
            },
            button_method: () => {
              closeModal();
              router.push({ name: "EventProfile" });
            }
          }
        });
      }
    }
  } else {
    await getTickets();
  }
  createNewPurchase();
};

const acceptWaitingList = async () => {
  if (isAuth.value) {
    showWaitingList.value = true;
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  } else {
    openModal(USER_NOLOGIN_INFO);
  }
};
const setPreferencesWaitingList = async preferences => {
  await addToWaitingList(event.value.id, preferences);
  event.value.user.waiting_weswap = true;
  showWaitingList.value = false;
  window.scrollTo({
    top: 0,
    behavior: "smooth"
  });
};
const deleteWaitingList = async () => {
  await addToWaitingList(event.value.id);
  event.value.user.waiting_weswap = false;
};

// Watch
watch(accessCodeValid, async (newValue, oldValue) => {
  if (newValue === true) {
    loading.value = true;
    await configurePurchase(route);
    loading.value = false;
  }
});
</script>

<style lang="scss" setup>
.iframe-logo-wegow {
  font-size: 8px;
  position: absolute;
  top: 10px;
  right: 10px;
  img {
    width: 12px;
    margin-left: 4px;
  }
}
.ticket-office-list {
  max-height: 85vh;
}
</style>
